import { QueryClientProvider } from '@tanstack/react-query';
import { type JSX, type ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { API_BASE_URL } from './lib/config';
import { makeQueryClient } from './lib/query-client';
import { BasketProvider } from './state/basket-provider';
import { CheckoutProvider } from './state/checkout';
import { LocaleProvider } from './state/locale';
import { SessionProvider, useLocalePersistence, useSession } from './state/session';
import './styles.css';

/**
 * Entry point. TanStack Query owns server state; component state stays local.
 *
 * The provider order is not arbitrary. Query is outermost because the locale
 * persistence hook and the session both need it. Locale comes next because the
 * session resolve carries the guest's preferred language (PRD GST-006). The
 * basket and the checkout draft come last because they are scoped to the QR
 * token the session resolved - one phone, one apartment, one basket.
 */
const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

/**
 * Open the connection the photographs will use before anything asks for one.
 *
 * Every picture on the storefront is fetched from the API origin, which is not
 * this page's origin (`order.` against `api.`), and an `<img>` opens its own
 * anonymous connection - separate from the one the session resolve opens with
 * CORS - so the first row otherwise pays DNS, TCP and TLS after the storefront
 * JSON has already arrived. Derived from the configured origin, never written
 * as a host (PRD OPS-018), and skipped when the API is same-origin (the dev
 * proxy), where there is nothing to open early.
 */
function preconnectMedia(): void {
  if (!/^https?:\/\//i.test(API_BASE_URL)) return;
  try {
    const origin = new URL(API_BASE_URL).origin;
    if (origin === globalThis.location.origin) return;
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    document.head.append(link);
  } catch {
    // An unparseable origin is the API client's problem to report, not this one's.
  }
}
preconnectMedia();

/**
 * Built by `lib/query-client`, not inline: the retry predicate and the network
 * mode there are what stop a refused request parking at `fetchStatus: 'paused'`
 * behind a spinner a guest cannot get out of. See that module.
 */
const queryClient = makeQueryClient();

function Providers({ children }: { children: ReactNode }): JSX.Element {
  // A language switch also patches the QR session, because the session's locale is
  // what notifications are sent in (PRD GST-006, NTF-004).
  const persistLocale = useLocalePersistence();
  return (
    <LocaleProvider onChange={persistLocale}>
      <SessionProvider>
        <ScopedState>{children}</ScopedState>
      </SessionProvider>
    </LocaleProvider>
  );
}

/**
 * Basket and draft are keyed by the QR token, never shared across apartments.
 *
 * The draft is also handed the session's next service day, because a printed code
 * outlives a morning: a guest who chose Sunday's window and comes back on Monday
 * must not have Monday's screens asking the server about Sunday (PRD GST-002).
 */
function ScopedState({ children }: { children: ReactNode }): JSX.Element {
  const { qrToken, session } = useSession();
  return (
    <BasketProvider scope={qrToken}>
      <CheckoutProvider scope={qrToken} earliestServiceDay={session?.nextServiceDay ?? null}>
        {children}
      </CheckoutProvider>
    </BasketProvider>
  );
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Providers>
        <App />
      </Providers>
    </QueryClientProvider>
  </StrictMode>,
);
